// The GA adapter (APP-2187): the single client-side seam through which the
// Vertical Video gallery emits analytics. It piggybacks the *host page's* own
// Google Analytics — because the gallery runs first-party inside the host's
// Shadow DOM (no iframe), our JS shares the host `window` and can see and call
// its `gtag` / `dataLayer`. We add nothing of our own: no PostHog, no
// first-party pipeline, no cookies or identifiers, and no consent handling (we
// inherit the host's Consent Mode state). See docs/research/ga4-embed-tracking-facts.md.

/**
 * The six events the gallery emits — lowercase snake_case under the
 * `crowdriff_vv_` prefix (a POC sub-namespace of gallery-app's `crowdRiff_*`
 * family), each clearing GA4's naming rules. This is the whole taxonomy.
 */
export const EVENTS = {
  /** Inline rail teaser first scrolls into view (once per rail mount). Passive. */
  railView: "crowdriff_vv_rail_view",
  /** Viewer opens the immersive feed. `entry` = card|see_all; `item_id` on a card. */
  feedOpen: "crowdriff_vv_feed_open",
  /** A clip is the active slide, dwelled ≥~1s. `item_id`, `source`. Passive. */
  videoImpression: "crowdriff_vv_video_impression",
  /** Viewer unmutes — the intent tier. `item_id`, `source`. */
  audioEnable: "crowdriff_vv_audio_enable",
  /** Active clip crosses a watch milestone. `item_id`, `source`, `percent`. Passive. */
  videoProgress: "crowdriff_vv_video_progress",
  /** Portal CTA tapped. `cta_label`, `cta_url`, `item_id`. */
  ctaClick: "crowdriff_vv_cta_click",
} as const;

/** GA4 event parameters. `undefined` values are dropped before sending. */
export type TrackParams = Record<string, string | number | boolean | undefined>;

type Gtag = (command: "event", name: string, params: Record<string, unknown>) => void;

/** A window-like host — only the two GA globals the cascade detects. */
interface HostGlobals {
  gtag?: unknown;
  dataLayer?: unknown;
}

// Drop undefined-valued params so an omitted field (e.g. `source`, or `item_id`
// on a "see all" open) never rides along as a literal `undefined`.
function clean(params: TrackParams): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

// The detection cascade (see the research note §1–2). Exactly one path per call:
//   gtag is a function → gtag('event', …)         — GA4 gtag.js Google tag
//   dataLayer is Array → dataLayer.push({event,…}) — GTM Custom Event shape
//   neither            → no-op                     — host runs no GA; nowhere to send
// Never both: a gtag-style push carries no `event` key so GTM would ignore it,
// and a bare event push is not how a gtag.js tag sends. `send` takes its host
// explicitly so the cascade is unit-testable without a real `window`.
export function send(host: HostGlobals | undefined, name: string, params: TrackParams): void {
  if (!host) return;
  const payload = clean(params);
  if (typeof host.gtag === "function") {
    (host.gtag as Gtag)("event", name, payload);
    return;
  }
  if (Array.isArray(host.dataLayer)) {
    host.dataLayer.push({ event: name, ...payload });
  }
}

/** Emit one gallery event through the host page's GA, if any is present. */
export function track(name: string, params: TrackParams = {}): void {
  send(typeof window === "undefined" ? undefined : (window as HostGlobals), name, params);
}
